import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Trophy, Users, Calendar, Award, Instagram, User, Zap, Swords, Eye, Search, Wallet, Youtube } from 'lucide-react';
import { motion } from 'framer-motion';
import { API } from '@/App';

export default function Home() {
  const [user, setUser] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [siteSettings, setSiteSettings] = useState({ apostados_enabled: true });

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('session_token');
      if (token) {
        try {
          const response = await fetch(`${API}/auth/me`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            // Fetch wallet balance
            const wr = await fetch(`${API}/wallet`, { headers: { 'Authorization': `Bearer ${token}` } });
            if (wr.ok) { const wd = await wr.json(); setWalletBalance(wd.balance || 0); }
          }
        } catch (err) {
          console.error('Auth check failed:', err);
        }
      }
    };
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${API}/settings`);
        if (res.ok) setSiteSettings(await res.json());
      } catch (e) { /* silent */ }
    };
    checkAuth();
    fetchSettings();
  }, []);

  const formatYtDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(`${dateStr}T12:00:00-03:00`);
      const weekday = d.toLocaleDateString('pt-BR', { weekday: 'long', timeZone: 'America/Sao_Paulo' }).toUpperCase();
      const dm = d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' });
      return `${weekday} • ${dm}`;
    } catch { return ''; }
  };

  return (
    <div className="min-h-screen bg-obsidian">
      {/* Header */}
      <header className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_esports-league-5/artifacts/0z14gmwv_logo.png" 
                alt="SEXTOUCUP" 
                className="h-14 w-14"
              />
              <h1 className="text-2xl font-unbounded font-bold text-white mr-8">SEXTOUCUP</h1>
            </div>
            <nav className="flex items-center gap-6 ml-auto">
              <Link to="/rankings" className="text-gold-400 hover:text-gold-300 transition-colors font-semibold" data-testid="nav-rankings-link">
                🏆 Hall da Fama
              </Link>
              <Link to="/standings" className="text-zinc-400 hover:text-white transition-colors" data-testid="nav-standings-link">
                Classificação
              </Link>
              <Link to="/confrontos" className="text-zinc-400 hover:text-white transition-colors" data-testid="nav-confrontos-link">
                Confrontos
              </Link>
              <Link to="/about" className="text-zinc-400 hover:text-white transition-colors" data-testid="nav-about-link">
                Sobre
              </Link>
              <Link to="/rules" className="text-zinc-400 hover:text-white transition-colors" data-testid="nav-rules-link">
                Regras
              </Link>
              <Link to="/discord" className="text-zinc-400 hover:text-white transition-colors" data-testid="nav-discord-link">
                Discord
              </Link>
              <a 
                href="https://www.instagram.com/sextoucup/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white font-semibold px-4 py-2 rounded-lg hover:opacity-90 transition-all transform hover:scale-105"
                data-testid="nav-instagram-link"
              >
                <Instagram className="h-5 w-5" />
                Instagram
              </a>
              {user ? (
                <>
                  {user.is_admin && (
                    <Link 
                      to="/dashboard" 
                      className="flex items-center gap-2 bg-gold-400 text-obsidian font-bold px-4 py-2 rounded-lg hover:bg-gold-300 transition-all"
                      data-testid="nav-dashboard-link"
                    >
                      DASHBOARD
                    </Link>
                  )}
                  {walletBalance !== null && walletBalance > 0 && (
                    <Link
                      to="/perfil"
                      className="flex items-center gap-1.5 bg-green-500/15 border border-green-500/30 text-green-400 px-3 py-1.5 rounded-lg font-bold text-sm hover:bg-green-500/25 transition-all"
                      data-testid="nav-wallet-balance"
                    >
                      <Wallet className="h-4 w-4" />
                      R${walletBalance.toFixed(2)}
                    </Link>
                  )}
                  <Link 
                    to="/perfil" 
                    className="flex items-center gap-2 text-gold-400 hover:text-gold-300 transition-colors font-semibold"
                    data-testid="nav-perfil-link"
                  >
                    <User className="h-5 w-5" />
                    Meu Perfil
                  </Link>
                  <button
                    onClick={() => {
                      localStorage.removeItem('session_token');
                      setUser(null);
                    }}
                    className="text-zinc-400 hover:text-white transition-colors"
                  >
                    Sair
                  </button>
                </>
              ) : (
                <>
                  <Link 
                    to="/login" 
                    className="text-zinc-400 hover:text-white transition-colors"
                    data-testid="nav-login-link"
                  >
                    Entrar
                  </Link>
                  <Link 
                    to="/register" 
                    className="bg-gold-400 text-obsidian font-bold px-6 py-2 hover:bg-gold-300 transition-all duration-300 uppercase tracking-wider gold-glow-hover"
                    data-testid="nav-register-link"
                  >
                    Cadastrar
                  </Link>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero-bg min-h-[80vh] flex items-center relative overflow-hidden noise-texture">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="flex justify-center mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_esports-league-5/artifacts/0z14gmwv_logo.png" 
                alt="Trophy" 
                className="h-24 w-24"
              />
            </div>
            <h1 className="text-5xl md:text-7xl font-unbounded font-bold tracking-tighter uppercase text-white mb-6">
              SEXTOUCUP
            </h1>
            <p className="text-xl md:text-2xl text-zinc-400 mb-4 font-manrope">
              Campeonato Oficial de Pro Clubs
            </p>
            <p className="text-lg md:text-xl text-gold-400 mb-12 font-mono uppercase tracking-widest">
              FC 26 e FC 27 • Nova e Antiga Geração
            </p>

            {/* YouTube Special Edition - Center CTA */}
            {siteSettings.youtube_edition_active && (
              <div className="max-w-4xl mx-auto mb-10" data-testid="youtube-edition-banner">
                <div className="rounded-2xl border-2 border-[#FF0000] bg-[#FF0000]/10 p-6 sm:p-8 text-center relative overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#FF0000]/10 to-transparent animate-pulse" />
                  <div className="relative">
                    <div className="flex items-center justify-center gap-3 mb-2">
                      <Youtube className="h-8 w-8 text-[#FF0000]" />
                      <h2 className="text-xl sm:text-3xl font-unbounded font-bold text-[#FF0000] uppercase">
                        SQUAD - EDIÇÃO ESPECIAL YOUTUBE!
                      </h2>
                    </div>
                    <p className="text-white font-semibold text-sm sm:text-base mb-5 uppercase tracking-widest">
                      {formatYtDate(siteSettings.youtube_edition_date)}{siteSettings.youtube_edition_time ? ` • ${siteSettings.youtube_edition_time}H` : ''} • 32 EQUIPES • VAGAS LIMITADAS
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                      <Link
                        to="/register-team?edicao=youtube"
                        data-testid="youtube-edition-cta"
                        className="inline-block bg-[#FF0000] text-white font-unbounded font-bold px-10 py-4 rounded-full uppercase tracking-wider text-sm sm:text-base hover:bg-[#d90000] hover:scale-105 transition-all shadow-lg shadow-red-600/40 animate-pulse"
                      >
                        Inscreva-se Agora →
                      </Link>
                      <Link
                        to="/edicao-youtube"
                        data-testid="youtube-edition-info-link"
                        className="inline-block border-2 border-[#FF0000] text-[#FF0000] font-unbounded font-bold px-8 py-3.5 rounded-full uppercase tracking-wider text-xs sm:text-sm hover:bg-[#FF0000] hover:text-white transition-all"
                      >
                        Como Funciona?
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Two columns: SEXTOUCUP left, SEXTOUAPOSTADOS right */}
            <div className={`grid grid-cols-1 ${siteSettings.apostados_enabled !== false ? 'md:grid-cols-2' : ''} gap-8 max-w-4xl mx-auto`}>
              {/* SEXTOUCUP */}
              <div className="glass-card rounded-2xl p-8 border border-gold-400/20 hover:border-gold-400/40 transition-all">
                <Trophy className="h-12 w-12 text-gold-400 mx-auto mb-4" />
                <h2 className="text-2xl font-unbounded font-bold text-gold-400 mb-2">SEXTOUCUP</h2>
                <p className="text-zinc-400 text-sm mb-6">Campeonato Oficial</p>
                <div className="space-y-3">
                  <Link 
                    to="/register-team" 
                    className="block bg-gold-400 text-obsidian font-bold px-6 py-3 text-center hover:bg-gold-300 transition-all uppercase tracking-wider gold-glow-hover"
                    data-testid="hero-register-team-button"
                  >
                    Cadastrar Time
                  </Link>
                  <Link 
                    to="/standings" 
                    className="block border-2 border-gold-400/30 text-gold-400 px-6 py-3 text-center hover:bg-gold-400/10 transition-all uppercase tracking-wider"
                    data-testid="hero-view-standings-button"
                  >
                    Ver Classificação
                  </Link>
                </div>
              </div>

              {/* SEXTOUAPOSTADOS - Only show when enabled */}
              {siteSettings.apostados_enabled !== false && (
              <div className="glass-card rounded-2xl p-8 border border-yellow-500/20 hover:border-yellow-500/40 transition-all" data-testid="apostados-section">
                <Zap className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
                <h2 className="text-2xl font-unbounded font-bold text-yellow-400 mb-2">SEXTOUAPOSTADOS</h2>
                <p className="text-zinc-400 text-sm mb-6">Amistosos Apostados</p>
                <div className="space-y-3">
                  <Link 
                    to="/apostados" 
                    className="block bg-gold-400 text-obsidian font-bold px-6 py-3 text-center hover:bg-gold-300 transition-all uppercase tracking-wider gold-glow-hover"
                    data-testid="hero-apostados-button"
                  >
                    <Swords className="h-4 w-4 inline mr-2" />
                    Apostar Agora
                  </Link>
                  <Link 
                    to="/apostados-disponiveis" 
                    className="block border-2 border-yellow-500/30 text-yellow-400 px-6 py-3 text-center hover:bg-yellow-500/10 transition-all uppercase tracking-wider"
                    data-testid="hero-ver-apostados-disponiveis-button"
                  >
                    <Search className="h-4 w-4 inline mr-2" />
                    Ver Apostados Disponiveis
                  </Link>
                  {user && (
                    <Link 
                      to="/meus-apostados" 
                      className="block border-2 border-yellow-500/30 text-yellow-400 px-6 py-3 text-center hover:bg-yellow-500/10 transition-all uppercase tracking-wider"
                      data-testid="hero-ver-meus-apostados-button"
                    >
                      <Eye className="h-4 w-4 inline mr-2" />
                      Ver Meus Apostados
                    </Link>
                  )}
                  <Link 
                    to="/apostados-ranking" 
                    className="block border-2 border-gold-400/30 text-gold-400 px-6 py-3 text-center hover:bg-gold-400/10 transition-all uppercase tracking-wider"
                    data-testid="hero-ranking-apostados-button"
                  >
                    <Trophy className="h-4 w-4 inline mr-2" />
                    Ranking Apostados
                  </Link>
                </div>
              </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 bg-obsidian-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-5xl font-unbounded font-semibold tracking-tight text-white text-center mb-16">
            Sobre o Campeonato
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="glass-card p-8 rounded-2xl card-hover group"
            >
              <Trophy className="h-12 w-12 text-gold-400 mb-4" />
              <h3 className="text-xl font-unbounded font-medium text-white mb-2">Competição</h3>
              <p className="text-zinc-400 font-manrope leading-relaxed">
                Disputas acirradas entre os melhores times de Pro Clubs
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="glass-card p-8 rounded-2xl card-hover group"
            >
              <Users className="h-12 w-12 text-gold-400 mb-4" />
              <h3 className="text-xl font-unbounded font-medium text-white mb-2">Comunidade</h3>
              <p className="text-zinc-400 font-manrope leading-relaxed">
                Conecte-se com jogadores apaixonados por Pro Clubs
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="glass-card p-8 rounded-2xl card-hover group"
            >
              <Calendar className="h-12 w-12 text-gold-400 mb-4" />
              <h3 className="text-xl font-unbounded font-medium text-white mb-2">Organização</h3>
              <p className="text-zinc-400 font-manrope leading-relaxed">
                Fase de grupos, estatísticas ao vivo e muito mais
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="glass-card p-8 rounded-2xl card-hover group"
            >
              <Award className="h-12 w-12 text-gold-400 mb-4" />
              <h3 className="text-xl font-unbounded font-medium text-white mb-2">Glória</h3>
              <p className="text-zinc-400 font-manrope leading-relaxed">
                Conquiste títulos e entre para a história do SEXTOUCUP
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-obsidian relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gold-400/5 to-transparent"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-unbounded font-bold text-white mb-6">
            Pronto para Competir?
          </h2>
          <p className="text-lg text-zinc-400 mb-8 font-manrope">
            Cadastre seu time agora e participe do campeonato mais emocionante de Pro Clubs
          </p>
          <Link 
            to="/register-team" 
            className="inline-block bg-gold-400 text-obsidian font-bold px-10 py-4 text-lg hover:bg-gold-300 transition-all duration-300 uppercase tracking-wider gold-glow-hover"
            data-testid="cta-register-team-button"
          >
            Cadastrar Meu Time
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-zinc-600 font-manrope">
            &copy; 2025 SEXTOUCUP. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
